import { useState, useCallback } from 'react'
import { SprintStatus } from '../../../../../lib/functions/sprint'
import { COLLAPSED_STATUSES } from '../../../../pages/ProductBacklog/components/SprintAssignmentDialog/types'
import { TeamSprintGroup } from './sprintGroupTypes'

const toKey = (teamUuid: string, status: SprintStatus) =>
  `${teamUuid}:${status}`

/**
 * チーム×ステータスの2階層で展開/折りたたみ状態を管理するフック
 * FINISHED, CANCELED はデフォルトで折りたたみ
 * 選択中のスプリントが属するグループは初期展開する
 */
export const useSprintGroupCollapse = (
  teamSprintGroups: TeamSprintGroup[],
  selectedUuids: string[] | undefined
) => {
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    const selectedSet = new Set(selectedUuids ?? [])

    for (const group of teamSprintGroups) {
      for (const status of COLLAPSED_STATUSES) {
        const hasSelectedSprint = group.sprints.some(
          s => s.status === status && selectedSet.has(s.uuid)
        )
        if (!hasSelectedSprint) {
          initial.add(toKey(group.teamUuid, status))
        }
      }
    }
    return initial
  })

  const isCollapsed = useCallback(
    (teamUuid: string, status: SprintStatus) =>
      collapsedKeys.has(toKey(teamUuid, status)),
    [collapsedKeys]
  )

  const toggle = useCallback((teamUuid: string, status: SprintStatus) => {
    setCollapsedKeys(prev => {
      const next = new Set(prev)
      const key = toKey(teamUuid, status)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  return { isCollapsed, toggle }
}
